import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { getDeviceId } from "@/lib/device";
import { formatMoney } from "@/lib/money";
import { toast } from "sonner";
import { Loader2, Sparkles, Coins, DollarSign } from "lucide-react";

// Display order MUST match server slot_index 0..5 so the wheel lands on the right wedge.
const SLOTS = [
  { label: "5 pts",   sub: "Common",   color: "hsl(var(--secondary))",    text: "hsl(var(--secondary-foreground))", icon: Coins },
  { label: "10 pts",  sub: "Common",   color: "hsl(var(--muted))",        text: "hsl(var(--muted-foreground))",     icon: Coins },
  { label: "25 pts",  sub: "Uncommon", color: "hsl(var(--accent))",       text: "hsl(var(--accent-foreground))",    icon: Sparkles },
  { label: "$0.05",   sub: "Rare",     color: "hsl(var(--primary))",      text: "hsl(var(--primary-foreground))",   icon: DollarSign },
  { label: "$0.10",   sub: "Rare",     color: "hsl(var(--primary))",      text: "hsl(var(--primary-foreground))",   icon: DollarSign },
  { label: "$0.50",   sub: "Jackpot",  color: "hsl(var(--destructive))",  text: "hsl(var(--destructive-foreground))", icon: DollarSign },
];

interface SpinResult {
  slot_index: number;
  prize_type: "points" | "cash" | "nothing";
  prize_label: string;
  points_awarded: number;
  cash_cents: number;
  spins_used_today: number;
  spins_remaining: number;
}

const SLOT_DEG = 360 / SLOTS.length;

const Spin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [spinning, setSpinning] = useState(false);
  const [angle, setAngle] = useState(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [points, setPoints] = useState<number | null>(null);
  const [lastPrize, setLastPrize] = useState<SpinResult | null>(null);
  const wheelRef = useRef<HTMLDivElement>(null);

  const loadStatus = async () => {
    if (!user) return;
    const [{ data: rem }, { data: w }] = await Promise.all([
      supabase.rpc("spins_remaining"),
      supabase.from("wallets").select("points_balance").eq("user_id", user.id).maybeSingle(),
    ]);
    setRemaining(typeof rem === "number" ? rem : 0);
    setPoints(((w as any)?.points_balance as number | undefined) ?? 0);
  };

  useEffect(() => {
    loadStatus();
     
  }, [user]);

  const handleSpin = async () => {
    if (spinning) return;
    if (remaining !== null && remaining <= 0) {
      toast.error("No spins left today. Come back tomorrow!");
      return;
    }
    setSpinning(true);
    setLastPrize(null);

    const { data, error } = await supabase.functions.invoke("spin-wheel", {
      body: { device_id: getDeviceId() },
    });
    const errMsg = (error as any)?.message || (data as any)?.error;
    if (errMsg || !data?.result) {
      setSpinning(false);
      toast.error(errMsg || "Spin failed");
      return;
    }

    const result = data.result as SpinResult;
    // Land pointer (top, 0deg) on the middle of the winning wedge.
    // Wheel rotates clockwise; wedge i center sits at i*SLOT_DEG + SLOT_DEG/2.
    const targetWedgeCenter = result.slot_index * SLOT_DEG + SLOT_DEG / 2;
    const fullSpins = 6; // visual flourish
    const finalAngle =
      Math.ceil(angle / 360) * 360 + fullSpins * 360 + (360 - targetWedgeCenter);
    setAngle(finalAngle);

    // Wait for animation to finish before showing the prize.
    setTimeout(() => {
      setSpinning(false);
      setLastPrize(result);
      setRemaining(result.spins_remaining);
      if (result.cash_cents > 0) {
        setPoints((p) => p ?? 0);
        toast.success(`🎉 You won ${formatMoney(result.cash_cents)}!`);
      } else {
        setPoints((p) => (p ?? 0) + result.points_awarded);
        toast.success(`+${result.points_awarded} points!`);
      }
    }, 4200);
  };

  const conicGradient = useMemo(() => {
    const stops = SLOTS.map((s, i) => {
      const start = i * SLOT_DEG;
      const end = (i + 1) * SLOT_DEG;
      return `${s.color} ${start}deg ${end}deg`;
    }).join(", ");
    return `conic-gradient(from 0deg, ${stops})`;
  }, []);

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Spin to Win</h1>
        <p className="text-center text-xs opacity-80 mt-1">Free daily reward · 3 spins per day</p>
      </BrandHeader>

      <div className="px-5 py-6 flex-1 flex flex-col items-center animate-fade-in-up">
        <div className="grid grid-cols-2 gap-3 w-full mb-6">
          <div className="rounded-2xl bg-secondary/60 p-3 text-center">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Spins left</p>
            <p className="font-display text-2xl font-bold">
              {remaining ?? "—"}<span className="text-sm font-normal text-muted-foreground">/3</span>
            </p>
          </div>
          <div className="rounded-2xl bg-secondary/60 p-3 text-center">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Your points</p>
            <p className="font-display text-2xl font-bold flex items-center justify-center gap-1">
              <Coins className="h-5 w-5 text-accent" />
              {points ?? "—"}
            </p>
          </div>
        </div>

        {/* Wheel */}
        <div className="relative w-72 h-72 mb-6 select-none">
          {/* Pointer */}
          <div
            className="absolute left-1/2 -top-2 -translate-x-1/2 z-10"
            aria-hidden
          >
            <div
              className="w-0 h-0 border-l-[14px] border-r-[14px] border-t-[22px] border-l-transparent border-r-transparent drop-shadow-md"
              style={{ borderTopColor: "hsl(var(--foreground))" }}
            />
          </div>

          {/* Wheel disc */}
          <div
            ref={wheelRef}
            className="absolute inset-0 rounded-full shadow-2xl ring-8 ring-background"
            style={{
              background: conicGradient,
              transform: `rotate(${angle}deg)`,
              transition: spinning ? "transform 4s cubic-bezier(0.16, 1, 0.3, 1)" : "none",
            }}
          >
            {SLOTS.map((s, i) => {
              const Icon = s.icon;
              const labelAngle = i * SLOT_DEG + SLOT_DEG / 2;
              return (
                <div
                  key={i}
                  className="absolute inset-0"
                  style={{ transform: `rotate(${labelAngle}deg)` }}
                >
                  <div
                    className="absolute left-1/2 top-6 -translate-x-1/2 flex flex-col items-center gap-1"
                    style={{ color: s.text }}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="font-display font-bold text-xs whitespace-nowrap">
                      {s.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hub */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-background shadow-xl ring-4 ring-foreground/10 grid place-items-center">
            <Sparkles className="h-7 w-7 text-accent" />
          </div>
        </div>

        {lastPrize && !spinning && (
          <div className="w-full mb-5 rounded-2xl border border-accent/40 bg-accent/10 p-4 text-center animate-fade-in-up">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">You won</p>
            <p className="font-display text-3xl font-extrabold mt-1">{lastPrize.prize_label}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {lastPrize.cash_cents > 0 ? "Added to your wallet" : "Added to your points"}
            </p>
          </div>
        )}

        <Button
          variant="hero"
          size="xl"
          className="w-full"
          disabled={spinning || (remaining !== null && remaining <= 0)}
          onClick={handleSpin}
        >
          {spinning ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : remaining !== null && remaining <= 0 ? (
            "No spins left today"
          ) : (
            "Spin the Wheel"
          )}
        </Button>

        <p className="text-[11px] text-muted-foreground text-center mt-4 px-4">
          Rewards are random, server-determined, and capped daily. Cash prizes are limited to a global daily pool.
        </p>
      </div>

      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default Spin;
